import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiClient } from '../utils/apiClient';
import { Invitation, UserProfile } from '../types';

const SessionInvitationsPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchInvitations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const fetchInvitations = async () => {
    try {
      setFetchLoading(true);
      setError(null);
      const response = await apiClient.get(`/v1/events/${eventId}/invitations/session`);
      setInvitations(response.data.data || []);
    } catch (err: any) {
      console.error('Error fetching invitations:', err);
      setError(err.response?.data?.error?.message || 'Failed to load invitations');
    } finally {
      setFetchLoading(false);
    }
  };

  const searchUsers = async () => {
    if (!searchQuery.trim()) {
      setUsers([]);
      return;
    }

    try {
      const response = await apiClient.get('/v1/users/search', {
        params: { query: searchQuery }
      });
      setUsers(response.data.data || []);
    } catch (err: any) {
      console.error('Error searching users:', err);
    }
  };

  const handleInvite = async () => {
    if (!selectedUserId) {
      alert('Please select a user to invite');
      return;
    }

    try {
      setLoading(true);
      await apiClient.post(`/v1/events/${eventId}/invitations/session`, {
        invitee_id: selectedUserId
      });
      setSelectedUserId('');
      setSearchQuery('');
      setUsers([]);
      await fetchInvitations();
    } catch (err: any) {
      console.error('Error sending invitation:', err);
      alert(err.response?.data?.error?.message || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: 'bg-warning',
      accepted: 'bg-success',
      rejected: 'bg-danger'
    };
    return badges[status as keyof typeof badges] || 'bg-secondary';
  };

  if (fetchLoading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="mb-3">
        <Link to={`/events/${eventId}`} className="btn btn-link text-decoration-none">
          <i className="fas fa-arrow-left me-2"></i>
          Back to Event
        </Link>
      </div>

      <h2 className="mb-4">
        <i className="fas fa-user-plus me-2"></i>
        Session Invitations
      </h2>

      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </div>
      )}

      <div className="card mb-4">
        <div className="card-header bg-white">
          <h5 className="mb-0">Invite Speaker</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-8 mb-3">
              <label htmlFor="searchQuery" className="form-label">
                Search for a speaker
              </label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  id="searchQuery"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or email"
                  onKeyPress={(e) => e.key === 'Enter' && searchUsers()}
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={searchUsers}
                >
                  <i className="fas fa-search"></i>
                </button>
              </div>
            </div>
          </div>

          {users.length > 0 && (
            <div className="mb-3">
              <label className="form-label">Select a user to invite:</label>
              <div className="list-group">
                {users.map(user => (
                  <button
                    key={user.user_id}
                    type="button"
                    className={`list-group-item list-group-item-action ${
                      selectedUserId === user.user_id ? 'active' : ''
                    }`}
                    onClick={() => setSelectedUserId(user.user_id)}
                  >
                    <div className="d-flex align-items-center">
                      {user.profile_image_url ? (
                        <img
                          src={user.profile_image_url}
                          alt={user.name}
                          className="rounded-circle me-2"
                          style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                        />
                      ) : (
                        <div
                          className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center me-2"
                          style={{ width: '40px', height: '40px' }}
                        >
                          <i className="fas fa-user"></i>
                        </div>
                      )}
                      <div>
                        <div className="fw-bold">{user.name}</div>
                        <small className={selectedUserId === user.user_id ? 'text-white-50' : 'text-muted'}>
                          {user.email}
                        </small>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            className="btn btn-primary"
            onClick={handleInvite}
            disabled={!selectedUserId || loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Sending...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane me-2"></i>
                Send Invitation
              </>
            )}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header bg-white">
          <h5 className="mb-0">Sent Invitations</h5>
        </div>
        <div className="card-body">
          {invitations.length === 0 ? (
            <p className="text-muted text-center py-3">
              No invitations sent yet
            </p>
          ) : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Speaker</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map(invitation => (
                    <tr key={invitation.invitation_id}>
                      <td>{invitation.invitee_name || 'Unknown'}</td>
                      <td className="text-muted">{invitation.invitee_id}</td>
                      <td>
                        <span className={`badge ${getStatusBadge(invitation.status)}`}>
                          {invitation.status}
                        </span>
                      </td>
                      <td className="text-muted">
                        {new Date(invitation.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionInvitationsPage;
